import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FlaskConical, AlertTriangle, RefreshCw, Play, Pause, Square } from "lucide-react";
import { api, fmtTime, fmtUsd } from "@/lib/api";
import { SectionTitle, Badge, StatCard } from "@/components/Bits";
import EngineHealthBanner from "@/components/EngineHealthBanner";
import { countEffectiveConditions, summarizeGroups } from "@/lib/profileSummary";

// Row-level decision mapper
function decisionKind(d) {
  if (d === "SIGNAL_LONG") return "long";
  if (d === "SIGNAL_SHORT") return "short";
  if (d === "NO_SIGNAL") return "off";
  if (d === "ERROR") return "warn";
  return "neutral";
}
function actionKind(a) {
  if (a === "order_placed") return "on";
  if (a === "blocked" || a === "order_failed") return "warn";
  return "off";
}
function fmtNum(n, d = 2) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

export default function SignalValidation() {
  const [status, setStatus] = useState(null);
  const [evals, setEvals] = useState([]);
  const [execs, setExecs] = useState([]);
  const [botStatus, setBotStatus] = useState(null);
  const [acting, setActing] = useState(false);

  const loadAll = async () => {
    try {
      const [s, e, ex, b] = await Promise.all([
        api.validationPhaseStatus(),
        api.validationPhaseEvaluations(20),
        api.testnetExecutionLogs(20),
        api.status(),
      ]);
      setStatus(s);
      setEvals(e || []);
      setExecs(ex || []);
      setBotStatus(b);
    } catch (err) {
      // silent auto-refresh; don't spam toasts
      console.warn("[validation] load failed", err?.message);
    }
  };

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, 4000);
    return () => clearInterval(id);
  }, []);

  const onEnable = async () => {
    try {
      setActing(true);
      const s = await api.validationPhaseEnable();
      setStatus(s);
      toast.success("Auto-execution enabled · Testnet");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setActing(false); }
  };

  const onDisable = async () => {
    try {
      setActing(true);
      const s = await api.validationPhaseDisable();
      setStatus(s);
      toast.success("Validation phase disabled");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setActing(false); }
  };

  const onStart = async () => {
    try { await api.start(); toast.success("Bot started"); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const onStop = async () => {
    try { await api.stop(); toast.success("Bot stopped"); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const onPause = async () => {
    try { await api.pause(); toast.success("Bot paused"); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const onClear = async () => {
    if (!window.confirm("Clear all evaluation logs?")) return;
    try { await api.validationPhaseClearEvaluations(); toast.success("Evaluations cleared"); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  if (!status) {
    return <div className="p-6 text-sm text-[#A0A0AB]">Loading…</div>;
  }

  const enabled = status.enabled;
  const engineLive = status.engine_active;
  const mode = botStatus?.bot_settings?.bot_mode;
  const runtimeState = botStatus?.runtime?.state || status.runtime_state;
  const modeMismatch = enabled && mode !== "testnet";

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <SectionTitle
        sub="Phase 1: hard-locked harness to prove end-to-end automated execution on Binance Futures Testnet. Every parameter is a compile-time constant — this panel is read-only except for the master toggle."
      >
        Signal Validation · Phase 1
      </SectionTitle>

      <EngineHealthBanner />

      {/* Phase toggle + hardcoded caps */}
      <div
        data-testid="phase-toggle-card"
        className="bg-[#0F0F14] border border-border rounded-sm p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div className="flex items-start gap-3">
          <FlaskConical size={18} strokeWidth={1.5} className="text-[#00E676] mt-0.5" />
          <div>
            <div className="text-sm font-semibold">
              Automated Validation Mode · {enabled ? (
                <span className="text-[#00E676]" data-testid="phase-status-label">ENABLED</span>
              ) : (
                <span className="text-[#71717A]" data-testid="phase-status-label">DISABLED</span>
              )}
            </div>
            <div className="text-xs text-[#A0A0AB] mt-1">
              Engine is {engineLive ? (
                <span className="text-[#00E676]">live</span>
              ) : (
                <span className="text-[#A0A0AB]">idle</span>
              )} · bot_mode=<span className="font-mono">{mode || "—"}</span> · runtime=<span className="font-mono">{runtimeState}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {enabled ? (
            <Button
              data-testid="phase-disable-btn"
              onClick={onDisable}
              disabled={acting}
              variant="outline"
              className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]"
            >
              Disable Phase
            </Button>
          ) : (
            <Button
              data-testid="phase-enable-btn"
              onClick={onEnable}
              disabled={acting}
              className="rounded-sm bg-[#00E676] hover:bg-[#00C766] text-black font-semibold"
            >
              Enable Phase
            </Button>
          )}
        </div>
      </div>

      {modeMismatch ? (
        <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-sm p-3 flex items-start gap-2 text-xs text-[#F59E0B]">
          <AlertTriangle size={14} strokeWidth={1.5} className="mt-0.5" />
          <div>
            <div className="font-semibold">bot_mode is not "testnet"</div>
            <div className="text-[#F59E0B]/80 normal-case">Switch to Binance Testnet in Bot Settings and start the bot for the validation engine to fire.</div>
          </div>
        </div>
      ) : null}

      {/* Active strategy */}
      <div
        data-testid="active-strategy-card"
        className={`rounded-sm p-4 border ${
          status.active_profile
            ? "bg-[#0F0F14] border-[#00E676]/30"
            : "bg-[#1A1A22] border-border"
        }`}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Active strategy</div>
            {status.active_profile ? (
              <>
                <div className="text-sm font-semibold text-[#00E676] mt-1">
                  {status.active_profile.name}
                </div>
                <div className="text-xs text-[#A0A0AB] mt-0.5 font-mono">
                  {(() => {
                    // Mirror the live banner via the shared
                    // profileSummary helper so flat and composite
                    // profiles count identically across surfaces.
                    const p = status.active_profile;
                    const total = countEffectiveConditions(p);
                    const groups = summarizeGroups(p);
                    if (groups.length > 0) {
                      return `${p.combinator || "AND"} · ${groups.length} groups · ${total} conditions · ${p.timeframe}`;
                    }
                    return `${p.combinator || "AND"} · ${total} conditions · ${p.timeframe}`;
                  })()}
                  {" · "}cooldown {status.active_profile.cooldown_minutes}m
                  {" · "}LONG {status.active_profile.allow_long ? "✓" : "—"}
                  {" · "}SHORT {status.active_profile.allow_short ? "✓" : "—"}
                </div>
              </>
            ) : (
              <div className="text-sm text-[#A0A0AB] mt-1">
                <span className="font-semibold text-white">No active profile</span>
                {" — "}falling back to built-in hardcoded RSI harness.
              </div>
            )}
          </div>
          <a
            href="/strategies"
            data-testid="manage-strategies-link"
            className="text-xs text-[#00E676] underline decoration-dotted hover:decoration-solid"
          >
            Manage profiles →
          </a>
        </div>
      </div>

      {/* Hardcoded caps grid */}
      <div data-testid="phase-caps-grid" className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid="cap-symbol" label="Symbol" value={status.symbol} />
        <StatCard testid="cap-timeframe" label="Timeframe" value={status.timeframe} />
        <StatCard testid="cap-leverage" label="Leverage" value={`${status.leverage}×`} />
        <StatCard testid="cap-notional" label="Notional" value={`${fmtNum(status.notional_usdt, 0)} USDT`} />
        <StatCard testid="cap-order-type" label="Order type" value={status.order_type} />
        <StatCard testid="cap-max-pos" label="Max open LONG / SHORT" value={`${status.max_open_long_positions} / ${status.max_open_short_positions}`} />
        <StatCard testid="cap-cooldown" label="Cooldown" value={`${status.cooldown_minutes} min`} />
        <StatCard testid="cap-strategy" label="Strategy" value={status.active_profile?.name || "— (no profile)"} />
      </div>

      {/* Runtime status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <StatCard
          testid="runtime-open-positions"
          label="Open testnet positions"
          value={String(status.open_testnet_positions)}
          sublabel={`caps = ${status.max_open_long_positions}L / ${status.max_open_short_positions}S`}
          accent={status.open_testnet_positions >= (status.max_open_long_positions + status.max_open_short_positions) ? "text-[#F59E0B]" : ""}
        />
        <StatCard
          testid="runtime-cooldown"
          label="Cooldown"
          value={status.cooldown_active ? `${status.cooldown_seconds_remaining}s` : "off"}
          sublabel={status.cooldown_active ? "entries blocked" : "ready"}
          accent={status.cooldown_active ? "text-[#F59E0B]" : "text-[#00E676]"}
        />
        <StatCard
          testid="runtime-last-eval"
          label="Last evaluation"
          value={status.last_evaluation ? fmtTime(status.last_evaluation.timestamp) : "—"}
          sublabel={status.last_evaluation ? (status.last_evaluation.decision || "—") : "no evals yet"}
        />
        <StatCard
          testid="runtime-btc-price"
          label="BTC price"
          value={botStatus?.btc_price ? `$${fmtUsd(botStatus.btc_price)}` : "—"}
          sublabel={`kill switch: ${botStatus?.kill_switch_enabled ? "ON" : "OFF"}`}
        />
      </div>

      {/* Bot control (start/pause/stop) */}
      <div className="bg-[#0F0F14] border border-border rounded-sm p-4 flex items-center gap-2">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A] mr-2">Engine</div>
        <Button data-testid="bot-start-btn" size="sm" onClick={onStart} className="rounded-sm bg-[#00E676] hover:bg-[#00C766] text-black font-semibold">
          <Play size={12} strokeWidth={2} className="mr-1" /> Start
        </Button>
        <Button data-testid="bot-pause-btn" size="sm" variant="outline" onClick={onPause} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">
          <Pause size={12} strokeWidth={2} className="mr-1" /> Pause
        </Button>
        <Button data-testid="bot-stop-btn" size="sm" variant="outline" onClick={onStop} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">
          <Square size={12} strokeWidth={2} className="mr-1" /> Stop
        </Button>
        <div className="flex-1" />
        <Button data-testid="reload-btn" size="sm" variant="outline" onClick={loadAll} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">
          <RefreshCw size={12} strokeWidth={2} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* Evaluations table */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold tracking-tight">Last 20 evaluations</h3>
          <Button data-testid="clear-evals-btn" size="sm" variant="outline" onClick={onClear} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs">
            Clear evaluations
          </Button>
        </div>
        {evals.length === 0 ? (
          <div className="bg-[#0F0F14] border border-border rounded-sm p-6 text-sm text-[#71717A]">
            No evaluations yet. Enable the phase and start the bot — the engine will begin evaluating the active Strategy Profile every cycle.
          </div>
        ) : (
          <div data-testid="evaluations-table" className="bg-[#0F0F14] border border-border rounded-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-[#0A0A10] text-[10px] uppercase tracking-wider text-[#71717A]">
                  <th className="text-left px-3 py-2">Time</th>
                  <th className="text-left px-3 py-2">Strategy</th>
                  <th className="text-left px-3 py-2">Decision</th>
                  <th className="text-left px-3 py-2">Action</th>
                  <th className="text-right px-3 py-2">Close</th>
                  <th className="text-right px-3 py-2">RSI</th>
                  <th className="text-left px-3 py-2">Bullish pivots (price / RSI)</th>
                  <th className="text-left px-3 py-2">Bearish pivots (price / RSI)</th>
                  <th className="text-left px-3 py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {evals.map((r) => {
                  const b = r.bullish || {};
                  const s = r.bearish || {};
                  const pp = (prev, last) => prev == null ? "—" : `${fmtNum(prev)} → ${fmtNum(last)}`;
                  const rp = (prev, last) => prev == null ? "—" : `${fmtNum(prev, 1)} → ${fmtNum(last, 1)}`;
                  return (
                    <tr key={r.id} data-testid={`eval-row-${r.id}`} className="border-b border-border/50 hover:bg-[#1A1A22]/40">
                      <td className="px-3 py-2 font-mono text-[#A0A0AB]">{fmtTime(r.timestamp)}</td>
                      <td className="px-3 py-2 text-[11px]">
                        <div className="text-white truncate max-w-[140px]" title={r.strategy_name || "—"}>
                          {r.strategy_name || "—"}
                        </div>
                        <div className="text-[10px] text-[#71717A] font-mono">{r.signal_type || "—"}</div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge kind={decisionKind(r.decision)}>{r.decision || "—"}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Badge kind={actionKind(r.action_taken)}>{r.action_taken || "—"}</Badge>
                      </td>
                      <td className="px-3 py-2 font-mono text-right tabular-nums">
                        {r.last_close ? `$${fmtNum(r.last_close)}` : "—"}
                      </td>
                      <td className="px-3 py-2 font-mono text-right tabular-nums">
                        {r.last_rsi != null ? fmtNum(r.last_rsi, 1) : "—"}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-[#A0A0AB]">
                        {pp(b.price_pivot_prev, b.price_pivot_last)} / {rp(b.rsi_pivot_prev, b.rsi_pivot_last)}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-[#A0A0AB]">
                        {pp(s.price_pivot_prev, s.price_pivot_last)} / {rp(s.rsi_pivot_prev, s.rsi_pivot_last)}
                      </td>
                      <td className="px-3 py-2 text-[#A0A0AB] max-w-[280px]">
                        <div className="truncate" title={r.action_reason || r.reason}>{r.action_reason || r.reason || "—"}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Execution logs */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold tracking-tight">Last 20 execution logs</h3>
        {execs.length === 0 ? (
          <div className="bg-[#0F0F14] border border-border rounded-sm p-6 text-sm text-[#71717A]">
            No execution logs yet. Orders will appear here when the engine fires a trade.
          </div>
        ) : (
          <div data-testid="execution-logs-list" className="space-y-2">
            {execs.map((r) => (
              <div key={r.id} data-testid={`exec-row-${r.id}`} className="bg-[#0F0F14] border border-border rounded-sm p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Badge kind={r.ok ? "on" : "warn"}>{r.ok ? "OK" : "FAIL"}</Badge>
                    <Badge kind="neutral">{r.action || "—"}</Badge>
                    {r.side ? <Badge kind={r.side === "LONG" ? "long" : "short"}>{r.side}</Badge> : null}
                    <span className="text-[#A0A0AB] text-xs">{r.symbol}</span>
                  </div>
                  <div className="text-[11px] text-[#71717A] font-mono">
                    {r.latency_ms != null ? `${r.latency_ms} ms` : ""}
                    {r.retry_count ? ` · retries ${r.retry_count}` : ""}
                    <span className="ml-2">{fmtTime(r.timestamp)}</span>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">Request</div>
                    <pre className="bg-[#0A0A10] border border-border/60 rounded-sm p-2 overflow-x-auto text-[#A0A0AB] max-h-32">
{JSON.stringify(r.request || {}, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">Response</div>
                    <pre className="bg-[#0A0A10] border border-border/60 rounded-sm p-2 overflow-x-auto text-[#A0A0AB] max-h-32">
{JSON.stringify(r.response || {}, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
